import { create } from 'zustand';
import { Node, Edge } from 'reactflow';
import { WorkflowNode, WorkflowEdge, WorkflowVariable } from '../types';

interface WorkflowBuilderState {
  // Canvas state
  nodes: Node[];
  edges: Edge[];
  variables: WorkflowVariable[];
  selectedNode: Node | null;
  isDirty: boolean;

  // Workflow meta
  workflowId: string | null;
  workflowName: string;
  workflowDescription: string;
  workflowStatus: string;

  // Actions
  setNodes: (nodes: Node[]) => void;
  setEdges: (edges: Edge[]) => void;
  addNode: (node: Node) => void;
  removeNode: (id: string) => void;
  updateNodeData: (id: string, data: Record<string, unknown>) => void;
  setSelectedNode: (node: Node | null) => void;
  setVariables: (variables: WorkflowVariable[]) => void;
  addVariable: (variable: WorkflowVariable) => void;
  removeVariable: (name: string) => void;
  setWorkflowMeta: (meta: { id?: string; name?: string; description?: string; status?: string }) => void;
  markClean: () => void;
  reset: () => void;
  loadFromDefinition: (definition: { nodes: WorkflowNode[]; edges: WorkflowEdge[]; variables: WorkflowVariable[] }) => void;
}

const defaultState = {
  nodes: [],
  edges: [],
  variables: [],
  selectedNode: null,
  isDirty: false,
  workflowId: null,
  workflowName: 'New Workflow',
  workflowDescription: '',
  workflowStatus: 'draft',
};

export const useWorkflowStore = create<WorkflowBuilderState>((set, get) => ({
  ...defaultState,

  setNodes: (nodes) => set({ nodes, isDirty: true }),
  setEdges: (edges) => set({ edges, isDirty: true }),

  addNode: (node) => set((s) => ({ nodes: [...s.nodes, node], isDirty: true })),

  removeNode: (id) => set((s) => ({
    nodes: s.nodes.filter((n) => n.id !== id),
    edges: s.edges.filter((e) => e.source !== id && e.target !== id),
    selectedNode: s.selectedNode?.id === id ? null : s.selectedNode,
    isDirty: true,
  })),

  updateNodeData: (id, data) => set((s) => ({
    nodes: s.nodes.map((n) => n.id === id ? { ...n, data: { ...n.data, ...data } } : n),
    selectedNode: s.selectedNode?.id === id ? { ...s.selectedNode, data: { ...s.selectedNode.data, ...data } } : s.selectedNode,
    isDirty: true,
  })),

  setSelectedNode: (node) => set({ selectedNode: node }),

  setVariables: (variables) => set({ variables, isDirty: true }),

  addVariable: (variable) => set((s) => ({
    variables: [...s.variables.filter((v) => v.name !== variable.name), variable],
    isDirty: true,
  })),

  removeVariable: (name) => set((s) => ({
    variables: s.variables.filter((v) => v.name !== name),
    isDirty: true,
  })),

  setWorkflowMeta: (meta) => set((s) => ({
    workflowId: meta.id ?? s.workflowId,
    workflowName: meta.name ?? s.workflowName,
    workflowDescription: meta.description ?? s.workflowDescription,
    workflowStatus: meta.status ?? s.workflowStatus,
    isDirty: true,
  })),

  markClean: () => set({ isDirty: false }),

  reset: () => set(defaultState),

  loadFromDefinition: ({ nodes, edges, variables }) => {
    const rfNodes: Node[] = nodes.map((n) => ({
      id: n.id,
      type: n.type,
      position: n.position,
      data: { ...n.data, label: n.label, nodeType: n.type },
      draggable: true,
    }));

    const rfEdges: Edge[] = edges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      sourceHandle: e.sourceHandle,
      targetHandle: e.targetHandle,
      label: e.label,
      type: e.type === 'conditional' ? 'smoothstep' : 'smoothstep',
      animated: e.type === 'conditional',
      style: { stroke: e.type === 'error' ? '#ef4444' : '#6366f1', strokeWidth: 2 },
    }));

    set({ nodes: rfNodes, edges: rfEdges, variables, isDirty: false });
  },
}));
